import { RouteProps } from "@/mk2/apps/bazaar/containers/ProductDetail/ProductDetailPage";
import { ProductQuality } from "@/mk2/apps/bazaar/schemas";

export const REVIEW_FORM = (buyerId: number) => `reviewForm_${buyerId}`;
export const REVIEW_REPLY_FORM = (reviewId: number) => `reviewReplyForm_${reviewId}`;
export const SELLER_NOTES_FORM = (userId: number) => `sellerNotesForm_${userId}`;
export const MESSAGE_REPLY_FORM = (buyerId: number) => `messageReplyForm_${buyerId}`;
export const CONTACT_SELLER_FORM = (productId: number) => `contactSellerForm_${productId}`;

export function paramsToSlug(params: RouteProps["routeParams"]): string {
    return `${params.category}/${params.hash}/${params.title}`;
}

export function slugToParams(slug: string): RouteProps["routeParams"] {
    const parts = slug.split("/");
    return {
        category: parts[0],
        hash: parts[1],
        title: parts[2],
    };
}

const MULTISELECT_PROPS_WITH_QUALITY = [
    "new-p_size",
    "new-p_color",
    "handmade-p_size",
    "handmade-p_color",
    "services-p_age_years",
    "services-p_for_whom",
    "services-p_frequency",
    "services-p_level",
    "services-p_place",
    "services-p_school_subject",
    "services-p_space_type",
];

const MULTISELECT_PROPS = [
    "p_sex",
    "p_isofix",
    "p_seat_facing",
    "p_brand_models",
    "p_fits_on_model",
];

export function isMultiselectProperty(quality: ProductQuality, property: string): boolean {
    return (
        MULTISELECT_PROPS_WITH_QUALITY.includes(`${quality}-${property}`) ||
        MULTISELECT_PROPS.includes(property)
    );
}

// Snap scroll to an AdSlot when it enters the viewport (scrolling down). Each ad to be
// snapped must be wrapped in an element carrying `data-ad-snap`, inside `container`.
// Returns a teardown that removes listeners and timers.
export function attachAdSnap(container: HTMLElement): () => void {
    // Users who opt out of motion still get the ad snapped into view, but instantly.
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const snapBehavior: ScrollBehavior = reduceMotion ? "auto" : "smooth";

    const snapped = new Set<HTMLElement>();
    let isAnimating = false;
    let lastScrollY = window.scrollY;
    let rafId = 0;
    // Fallback timer in case `scrollend` never fires (older Safari, interrupted anim).
    let animFallback = 0;

    const endAnimation = () => {
        window.clearTimeout(animFallback);
        isAnimating = false;
        // Resync so the next direction check isn't skewed by the programmatic jump.
        lastScrollY = window.scrollY;
    };

    const onScroll = () => {
        // Ignore scroll events generated by our own programmatic snap.
        if (isAnimating) return;
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
            const y = window.scrollY;
            const scrollingDown = y > lastScrollY;
            lastScrollY = y;
            if (!scrollingDown) return;

            const adEls = container.querySelectorAll<HTMLElement>("[data-ad-snap]");
            for (const el of adEls) {
                if (snapped.has(el)) continue;
                const rect = el.getBoundingClientRect();
                if (rect.top >= 0 && rect.top < 150) {
                    snapped.add(el);
                    isAnimating = true;
                    window.scrollTo({ top: rect.top + y - 120, behavior: snapBehavior });
                    // `scrollend` clears the flag reliably; timer is just a safety net.
                    animFallback = window.setTimeout(endAnimation, 1500);
                    break;
                }
            }
        });
    };

    const onScrollEnd = () => {
        if (isAnimating) endAnimation();
    };

    // Any manual input aborts the snap so the user can scroll past freely.
    const onUserInput = () => {
        if (isAnimating) endAnimation();
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("scrollend", onScrollEnd, { passive: true });
    window.addEventListener("wheel", onUserInput, { passive: true });
    window.addEventListener("touchstart", onUserInput, { passive: true });
    window.addEventListener("keydown", onUserInput);
    return () => {
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("scrollend", onScrollEnd);
        window.removeEventListener("wheel", onUserInput);
        window.removeEventListener("touchstart", onUserInput);
        window.removeEventListener("keydown", onUserInput);
        cancelAnimationFrame(rafId);
        window.clearTimeout(animFallback);
        snapped.clear();
    };
}
